import React from "react";
import toast from "react-hot-toast";

interface InputFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: "text" | "email" | "tel" | "date";
  placeholder?: string;
  required?: boolean;
  error?: string;
  maxLength?: number;
  disabled?: boolean;
}

export function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  error,
  maxLength,
  disabled = false,
}: InputFieldProps) {
  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block text-sm font-semibold text-gray-700 mb-2"
      >
        {label}
        {required && <span className="text-red-600 ml-1 font-bold">*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        disabled={disabled}
        className={`w-full px-4 py-3 border rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 ${
          error
            ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
            : "border-gray-300 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
        } ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
      />
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">{error}</p>
      )}
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  error,
  disabled = false,
  placeholder = "Selecione...",
}: SelectFieldProps) {
  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block text-sm font-semibold text-gray-700 mb-2"
      >
        {label}
        {required && <span className="text-red-600 ml-1 font-bold">*</span>}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-3 border rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 ${
          error
            ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
            : "border-gray-300 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
        } ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">{error}</p>
      )}
    </div>
  );
}

interface FileUploadProps {
  label: string;
  name: string;
  onChange: (file: File | null) => void;
  required?: boolean;
  error?: string;
  accept?: string;
  maxSizeMB?: number;
  currentFileName?: string;
}

export function FileUpload({
  label,
  name,
  onChange,
  required = false,
  error,
  accept = ".pdf",
  maxSizeMB = 5,
  currentFileName,
}: FileUploadProps) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file && file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Arquivo muito grande! Tamanho máximo: ${maxSizeMB}MB`);
      e.target.value = "";
      onChange(null);
      return;
    }

    onChange(file);
  };

  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block text-sm font-semibold text-gray-700 mb-2"
      >
        {label}
        {required && <span className="text-red-600 ml-1 font-bold">*</span>}
      </label>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <label
          htmlFor={name}
          className="inline-flex items-center px-6 py-3 border border-blue-400 rounded-xl cursor-pointer hover:bg-blue-50 hover:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
        >
          <svg
            className="w-5 h-5 mr-2 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <span className="text-sm font-semibold text-blue-700">
            Selecionar Arquivo
          </span>
        </label>
        <input
          type="file"
          id={name}
          name={name}
          onChange={handleFileChange}
          accept={accept}
          className="hidden"
        />
        {currentFileName && (
          <span className="text-sm text-green-700 font-medium flex items-center bg-green-50 px-3 py-2 rounded-lg">
            ✓ {currentFileName}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-600 font-medium">
        Formato: PDF • Tamanho máximo: {maxSizeMB} MB
      </p>
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">{error}</p>
      )}
    </div>
  );
}
